import { DateTime, Interval } from 'luxon';
import type { TimeEntry } from './types';

export const DEFAULT_TZ = 'Europe/London';

export interface LocalResolution {
  /** UTC ISO instant */
  instant: string;
  /** True when the wall-clock time did not exist (spring-forward gap) and was moved forward. */
  adjusted: boolean;
  /** True when the wall-clock time occurs twice (autumn fall-back); the earlier (BST) instant is used. */
  ambiguous: boolean;
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::00)?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function normaliseTime(t: string): string {
  const m = TIME_RE.exec(t);
  if (!m) throw new Error(`Invalid time "${t}" (expected HH:mm)`);
  return `${m[1]}:${m[2]}`;
}

/** Convert a local wall-clock date+time in `zone` to a UTC instant, DST-safely. */
export function localToInstant(date: string, time: string, zone = DEFAULT_TZ): LocalResolution {
  if (!DATE_RE.test(date)) throw new Error(`Invalid date "${date}"`);
  const hhmm = normaliseTime(time);
  let dt = DateTime.fromISO(`${date}T${hhmm}`, { zone });
  if (!dt.isValid) throw new Error(`Invalid local time ${date} ${hhmm} in ${zone}`);
  const same = (x: DateTime) => x.toFormat('yyyy-MM-dd HH:mm') === `${date} ${hhmm}`;
  const adjusted = !same(dt);
  // Ambiguous if the same wall-clock also exists an hour earlier or later (clocks going back). Luxon picks one using
  // today's offset as its guess, so which one depends on when the code runs (t423u found this after 25 Oct 2026):
  // always take the first (summer-time) occurrence.
  let ambiguous = false;
  if (!adjusted) {
    const earlier = dt.minus({ hours: 1 });
    if (same(earlier) && earlier.offset !== dt.offset) { dt = earlier; ambiguous = true; }
    else { const later = dt.plus({ hours: 1 }); ambiguous = same(later) && later.offset !== dt.offset; }
  }
  return { instant: dt.toUTC().toISO()!, adjusted, ambiguous };
}

export interface ShiftInstants {
  starts_at: string;
  ends_at: string;
  overnight: boolean;
  duration_minutes: number;
  dst_notes: string[];
}

/**
 * Build UTC instants for a rota shift given its local date and wall-clock start/end.
 * end_time <= start_time means the shift finishes on the following local day.
 * Duration is computed from the instants, so an overnight shift spanning the October
 * clock change is one hour longer, and one spanning the March change one hour shorter.
 */
export function buildShiftInstants(localDate: string, startTime: string, endTime: string, zone = DEFAULT_TZ): ShiftInstants {
  const s = normaliseTime(startTime);
  const e = normaliseTime(endTime);
  const overnight = e <= s;
  const endDate = overnight ? DateTime.fromISO(localDate).plus({ days: 1 }).toISODate()! : localDate;
  const start = localToInstant(localDate, s, zone);
  const end = localToInstant(endDate, e, zone);
  const notes: string[] = [];
  if (start.adjusted) notes.push(`Start ${s} does not exist on ${localDate} (clocks go forward); moved to ${formatLocal(start.instant, zone, 'HH:mm')}.`);
  if (end.adjusted) notes.push(`End ${e} does not exist on ${endDate} (clocks go forward); moved to ${formatLocal(end.instant, zone, 'HH:mm')}.`);
  if (start.ambiguous) notes.push(`Start ${s} on ${localDate} occurs twice (clocks go back); the first (BST) occurrence is used.`);
  if (end.ambiguous) notes.push(`End ${e} on ${endDate} occurs twice (clocks go back); the first (BST) occurrence is used.`);
  const duration = minutesBetween(start.instant, end.instant);
  if (duration !== wallClockMinutes(s, e)) {
    notes.push(`Shift crosses a clock change: actual length ${formatMinutes(duration)} vs ${formatMinutes(wallClockMinutes(s, e))} on the clock.`);
  }
  return { starts_at: start.instant, ends_at: end.instant, overnight, duration_minutes: duration, dst_notes: notes };
}

export function wallClockMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let d = eh * 60 + em - (sh * 60 + sm);
  if (d <= 0) d += 24 * 60;
  return d;
}

export function minutesBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 60000);
}

export function formatMinutes(min: number): string {
  const sign = min < 0 ? '-' : '';
  const m = Math.abs(min);
  return `${sign}${Math.floor(m / 60)}h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}m` : ''}`;
}

export function formatLocal(instant: string, zone = DEFAULT_TZ, fmt = 'yyyy-MM-dd HH:mm'): string {
  return DateTime.fromISO(instant, { zone: 'utc' }).setZone(zone).toFormat(fmt);
}

export function toLocalDate(instant: string, zone = DEFAULT_TZ): string {
  return DateTime.fromISO(instant, { zone: 'utc' }).setZone(zone).toISODate()!;
}

export function toLocalTime(instant: string, zone = DEFAULT_TZ): string {
  return formatLocal(instant, zone, 'HH:mm');
}

/** Start of the rota week (default Monday) containing a local date. */
export function weekStart(localDate: string, weekStartsOn = 1): string {
  const d = DateTime.fromISO(localDate);
  const diff = (d.weekday - weekStartsOn + 7) % 7;
  return d.minus({ days: diff }).toISODate()!;
}

export function addDays(localDate: string, days: number): string {
  return DateTime.fromISO(localDate).plus({ days }).toISODate()!;
}

export function isoWeekday(localDate: string): number {
  return DateTime.fromISO(localDate).weekday;
}

/**
 * Move an instant by N calendar days keeping the same local wall-clock time
 * (used by "copy week" so 09:00 stays 09:00 across a DST change).
 */
export function shiftInstantByLocalDays(instant: string, days: number, zone = DEFAULT_TZ): string {
  return DateTime.fromISO(instant, { zone: 'utc' }).setZone(zone).plus({ days }).toUTC().toISO()!;
}

/** Age in whole years on a given date. */
export function ageOn(dateOfBirth: string, onDate: string): number {
  const dob = DateTime.fromISO(dateOfBirth);
  const d = DateTime.fromISO(onDate);
  let age = d.year - dob.year;
  if (d.month < dob.month || (d.month === dob.month && d.day < dob.day)) age--;
  return age;
}

/** Minutes of [aStart,aEnd) that fall inside the local night window 22:00-06:00. */
export function nightMinutes(aStart: string, aEnd: string, zone = DEFAULT_TZ, nightStart = '22:00', nightEnd = '06:00'): number {
  const start = DateTime.fromISO(aStart, { zone: 'utc' }).setZone(zone);
  const end = DateTime.fromISO(aEnd, { zone: 'utc' }).setZone(zone);
  const iv = Interval.fromDateTimes(start, end);
  let total = 0;
  let day = start.startOf('day').minus({ days: 1 });
  while (day < end) {
    const ns = DateTime.fromISO(`${day.toISODate()}T${nightStart}`, { zone });
    const ne = DateTime.fromISO(`${day.plus({ days: 1 }).toISODate()}T${nightEnd}`, { zone });
    const inter = iv.intersection(Interval.fromDateTimes(ns, ne));
    if (inter) total += inter.length('minutes');
    day = day.plus({ days: 1 });
  }
  return Math.round(total);
}

/** Worked/paid minutes for a time entry (open entries are measured to `now`). Unpaid breaks are deducted from paid time. */
export function entryMinutes(e: Pick<TimeEntry, 'clock_in_at' | 'clock_out_at' | 'breaks'> & Partial<Pick<TimeEntry, 'paid_in_at' | 'paid_out_at'>>, now = Date.now()) {
  // Clock-time rounding: pay runs from the rounded times when the entry has them (the real clock times are kept).
  const start = Date.parse(e.paid_in_at ?? e.clock_in_at);
  const end = e.clock_out_at ? Date.parse(e.paid_out_at ?? e.clock_out_at) : now;
  const gross = Math.max(0, (end - start) / 60000);
  let unpaid = 0, all = 0;
  for (const b of e.breaks ?? []) {
    const m = Math.max(0, ((b.ended_at ? Date.parse(b.ended_at) : Math.min(now, end)) - Date.parse(b.started_at)) / 60000);
    all += m;
    if (!b.paid) unpaid += m;
  }
  return { worked_minutes: Math.round(gross - all), paid_minutes: Math.round(gross - unpaid), unpaid_break_minutes: Math.round(unpaid) };
}

export const CLOCK_ROUND_OPTIONS = [0, 5, 10, 15] as const;
export type ClockRoundMode = 'nearest' | 'employee';
/** Mirrors workforce.round_clock(): nearest for both ends, or in the employee's favour (in rounds down, out rounds up). Never against the employee. */
export function roundClock(iso: string, minutes: number, mode: ClockRoundMode, end: 'in' | 'out'): string {
  if (!minutes) return iso;
  const step = minutes * 60000, t = Date.parse(iso);
  const f = mode === 'employee' ? (end === 'in' ? Math.floor : Math.ceil) : Math.round;
  return new Date(f(t / step) * step).toISOString();
}
